import React from 'react';
import type { UnitResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de unidade (view "Lista"). Padrão-lista travado: checkbox
 * de seleção (1ª col) + célula multi-linha (nome + código) + menu ••• (última
 * col). Avatar sem foto = slate.
 */
export interface UnitListItemProps {
  unit: UnitResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (unit: UnitResponse) => void;
  onDelete?: (unit: UnitResponse) => void;
  onToggleStatus?: (unit: UnitResponse) => void;
}

const formatLocation = (unit: UnitResponse): string | null => {
  const city = unit.address?.city?.trim();
  const state = unit.address?.state?.trim();
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  return null;
};

export const UnitListItem: React.FC<UnitListItemProps> = ({
  unit,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const isActive = unit.status === 'active';
  const location = formatLocation(unit);

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(unit) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(unit),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(unit), variant: 'danger', dividerBefore: true });

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`unit-row-${unit.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(unit.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`unit-check-${unit.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Unidade: avatar + nome */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            <AvatarFallback isSquircle>
              <Icon name="map-pin" className="w-4 h-4 text-white" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {unit.name}
            </div>
          </div>
        </div>
      </td>

      {/* Código */}
      <td className="px-4 py-3">
        <div className="text-sm truncate" style={{ color: 'var(--theme-muted-text)' }}>
          {unit.code || 'Sem código'}
        </div>
      </td>

      {/* Endereço */}
      <td className="px-4 py-3">
        <div className="text-sm truncate max-w-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {location ?? 'Não informado'}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`unit-toggle-${unit.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(unit)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`unit-status-${unit.id}`}
          />
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`unit-row-actions-${unit.id}`} />
      </td>
    </tr>
  );
};

UnitListItem.displayName = 'UnitListItem';
